import { useCallback } from 'react'
import { Interaction, InteractionModel } from 'types/shared'
import api from 'utils/api'
import useInitialStatus from 'status/shared/hooks/useInitialStatus'
import { useSearchParams } from 'react-router-dom'
import { useAppDispatch } from 'state/store'
import { interactionModelOperations } from '../redux'

interface UseSaveDraftInteractionParams {
  interactionModel: InteractionModel
}

const useSaveDraftInteraction = ({
  interactionModel
}: UseSaveDraftInteractionParams) => {
  const dispatch = useAppDispatch()

  const initialInteractionStatus = useInitialStatus({
    objectclassUuid: window.interactionObjectclassUuid
  })

  const [searchParams, setSearchParams] = useSearchParams()
  const existingDraftUuid = searchParams.get('draft_interaction_uuid') || ''

  const saveDraftInteraction = useCallback(
    async (update = false) => {
      if (
        !interactionModel.flatData?.uuid ||
        !window.interactionObjectclassUuid
      )
        return

      const payload: Partial<Interaction> = {
        uuid_interaction_model: interactionModel.flatData.uuid,
        state: JSON.stringify(interactionModel),
        uuid_Objectclass: window.interactionObjectclassUuid,
        date_start_row: interactionModel.dateStartInteraction,
        ...(!update && {
          situation: '10',
          uuid_status: initialInteractionStatus?.uuid
        }),
        ...(update && { version: interactionModel.draftVersion })
      }

      try {
        if (update) {
          if (!existingDraftUuid) {
            console.error('No existing draft UUID to update')
            return
          }

          if (!interactionModel.draftVersion) {
            console.error('Draft version is missing')
            return
          }

          return await api.patchObjects<Interaction>(
            payload,
            existingDraftUuid,
            false
          )
        } else {
          return await api.postObjects<Interaction>(payload, false)
        }
      } catch (error) {
        console.error('Failed to save draft interaction object:', error)
      }
    },
    [interactionModel, initialInteractionStatus, existingDraftUuid]
  )

  const createDraftInteraction = useCallback(async () => {
    const draftInteraction = await saveDraftInteraction(false)

    if (draftInteraction?.uuid) {
      setSearchParams(
        (oldSearchParams) => {
          oldSearchParams.set('draft_interaction_uuid', draftInteraction.uuid)
          return oldSearchParams
        },
        { replace: true }
      )
      dispatch(
        interactionModelOperations.setDraftVersion(draftInteraction?.version)
      )
    }
  }, [saveDraftInteraction, setSearchParams, dispatch])

  const updateDraftInteraction = useCallback(async () => {
    await saveDraftInteraction(true)

    const newVersion = Number(interactionModel.draftVersion) + 1
    dispatch(interactionModelOperations.setDraftVersion(newVersion.toString()))
  }, [saveDraftInteraction, dispatch, interactionModel.draftVersion])

  return {
    createDraftInteraction,
    updateDraftInteraction,
    saveDraftInteraction
  }
}

export default useSaveDraftInteraction
